<template>
  <button role="button" class="overflow-menu-button button" :tabindex="tabIndex" :class="cssClass">
    <cv-button
      v-click-outside="handleClickOutside"
      :kind="kind"
      :size="size"
      :disabled="disabled"
      :class="{ 'bx--btn--icon-only': iconOnly }"
      :tabindex="tabIndex"
      @click="handleButtonClick"
    >
      <!-- 3 dots for the loading animation -->
      <div v-if="dataLoading" class="dots">
        <span class="dot" />
        <span class="dot" />
        <span class="dot" />
      </div>

      <template v-else>
        <span>
          <slot />
        </span>
        <!-- Can only display ArrowDown24, as the toggle behavior is handled inside <overflow-menu>
        and it doesn't emit any event up to the parent. Using computed and checking for class in the DOM
        doesn't work as well. -->
        <svg-icon name="ArrowDown24" kind="action" />
      </template>
    </cv-button>
    <cv-tooltip v-if="showTooltip" :alignment="alignment" :direction="direction" :tip="tip">
    </cv-tooltip>
    <overflow-menu v-if="!dataLoading" ref="overflowMenu" :flip-menu="flipMenu" :up="up">
      <slot name="overflow-menu">
        <!-- Fallback content -->
        <OverflowMenuItem> Item 1</OverflowMenuItem>
        <OverflowMenuItem> Item 2</OverflowMenuItem>
      </slot>
    </overflow-menu>
  </button>
</template>

<script setup lang="ts">
import DOMUtils from "@utils/dom.utils.ts";
import { clickOutsideDirective } from "@utils/directives";
import { ref, computed, watch, useTemplateRef, nextTick } from "vue";
import { useVm } from "@utils/vm.ts";

// Props
interface Props {
  kind?: string;
  size?: string;
  tip?: string;
  direction?: string;
  alignment?: string;
  iconOnly?: boolean;
  disabled?: boolean;
  up?: boolean;
  loading?: boolean;
  flipMenu?: boolean;
  disableKeyboardNav?: boolean;
}

const {
  kind = "primary",
  size = "",
  tip = "",
  direction = "bottom",
  alignment = "center",
  iconOnly = false,
  disabled = false,
  up = false,
  loading = false,
  flipMenu = true,
  disableKeyboardNav = false,
} = defineProps<Props>();

const { vm } = useVm();

// Directives
const vClickOutside = clickOutsideDirective["click-outside"];

// Data
const dataLoading = ref(false);

// Template refs
const overflowMenuRef = useTemplateRef<HTMLElement>("overflowMenu");

// Computed
const cssClass = computed(() => {
  return {
    loading: dataLoading.value,
  };
});

const showTooltip = computed(() => {
  return tip && tip.trim() !== "" && disabled;
});

const tabIndex = computed(() => {
  return disableKeyboardNav ? -1 : 0;
});

// Methods
const handleButtonClick = (e) => {
  e.preventDefault();
  const tooltipTrigger = DOMUtils.elOf(vm.$el, ".bx--tooltip__trigger")[0];
  tooltipTrigger.click();
  // Set the class on click instead as Vue will compare the old vnode and existing classes will be removed
  // if it is added during the mount lifecycle hook
  nextTick(() => {
    setCssClassForOverflowMenu();
  });
};

const handleClickOutside = () => {
  if (overflowMenuRef.value && overflowMenuRef.value.open) {
    const tooltipTrigger = DOMUtils.elOf(vm.$el, ".bx--tooltip__trigger")[0];
    tooltipTrigger.click();
    // Set the class on click instead as Vue will compare the old vnode and existing classes will be removed
    // if it is added during the mount lifecycle hook
    nextTick(() => {
      setCssClassForOverflowMenu();
    });
  }
};

const setCssClassForOverflowMenu = () => {
  const overflowMenuButton = DOMUtils.elOf(vm.$el, ".bx--overflow-menu")[0];
  const overflowMenuButtonId = overflowMenuButton && overflowMenuButton.getAttribute("id");
  const overflowMenuOptions = DOMUtils.elOf(
    document.body,
    `:scope > .bx--overflow-menu-options[id="${overflowMenuButtonId}-menu"]`,
  )[0];

  if (up) {
    DOMUtils.addClass(overflowMenuOptions, "is-up");
  }
};

// Watch
watch(
  () => loading,
  () => {
    dataLoading.value = loading;
  },
);
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/typo";
@import "@styles/abstracts/mixins";
@import "@styles/abstracts/functions";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";

.overflow-menu-button {
  position: relative;
  display: inline-flex;
  max-width: 20rem;

  &:active,
  &:focus,
  &:hover {
    > .cv-tooltip.bx--tooltip__trigger {
      display: block;
    }
  }

  &.loading {
    .bx--btn {
      background-color: $ui-01 !important;
      color: $ui-01;
      border: 3px solid $interactive-01;
      pointer-events: none;
      user-select: none;
      animation: border-loading 2s ease-in-out infinite;

      // Use visibility hidden so that the length of the button is retained and it doesn't jump
      > span {
        visibility: hidden;
      }
    }

    .dots {
      display: flex;
      position: absolute;
      z-index: 1;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);

      ~ svg,
      ~ button {
        visibility: hidden;
      }
    }

    .dot {
      height: 0.5rem;
      width: 0.5rem;
      background-color: $interactive-01;
      border-radius: 100%;
      margin: $spacing-02;
      animation: dots-loading 2s ease-in-out infinite;

      &:nth-child(2) {
        animation-delay: 0.4s;
      }

      &:nth-child(3) {
        animation-delay: 0.8s;
      }
    }

    @keyframes dots-loading {
      50% {
        opacity: 0;
        transform: scale(0.7) translateY(10px);
      }
    }

    @keyframes border-loading {
      0% {
        border-color: tint($interactive-01, 50%) tint($interactive-01, 50%) $interactive-01
          $interactive-01;
      }
      50% {
        border-color: $interactive-01 $interactive-01 tint($interactive-01, 50%)
          tint($interactive-01, 50%);
      }
      100% {
        border-color: tint($interactive-01, 50%) tint($interactive-01, 50%) $interactive-01
          $interactive-01;
      }
    }
  }

  > .cv-tooltip.bx--tooltip__trigger {
    position: absolute;
    height: 100%;
    width: 100%;
    display: none;
    cursor: not-allowed;

    .bx--assistive-text {
      @include carbon--type-style("label-02", true);
    }

    &.bx--tooltip--bottom {
      &:before {
        bottom: 0.25rem;
      }

      .bx--assistive-text {
        bottom: 0;
      }
    }

    &.bx--tooltip--top {
      &:before {
        top: 0.3rem;
      }

      .bx--assistive-text {
        top: 0;
      }
    }
  }

  .bx--btn {
    &.bx--btn--icon-only {
      &.bx--btn--ghost {
        &:hover {
          svg {
            fill: transparent;
          }
        }
      }

      > span {
        min-width: initial;
      }

      .sls-icon {
        [stroke] {
          stroke: currentColor;
        }
      }
    }

    > span {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 5rem;
      height: calc(100% + 1rem);
    }

    > .sls-icon {
      margin: 0 (-$spacing-03) 0 $spacing-03;
    }

    [stroke] {
      stroke: currentColor;
    }
  }

  .bx--btn--field {
    max-height: 2.5rem;

    ~ .bx--overflow-menu {
      top: 0.7rem;
    }
  }

  .bx--btn--sm {
    max-height: 2rem;

    ~ .bx--overflow-menu {
      top: 0;
    }

    > span {
      height: calc(100% + 0.4rem);
    }
  }

  .bx--overflow-menu {
    position: absolute;
    top: 1rem;
    right: 0;
    visibility: hidden;
    width: 2rem !important;
    height: 2rem !important;
  }
}
</style>
